import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from "react";
import Input from "../Inputs/Input";
import { FiInfo } from "react-icons/fi";
import { IconButton, Tooltip } from "@material-ui/core";
import { BaseSelect, ContainerForm, ContainerGroup, FieldsLine, GroupTitle } from "./style";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import { useGlobalWebCheckinUser } from "../../../../hooks/WebCheckinUsers/global";
import { valueIsEmpty } from "../../../../services/generalServices";
import { IGroupProps } from "../../../../interfaces";
import { maskFunctions } from "../../../../services/maskServices";
import { Column, Label, Line } from "../../../../style";
import { inactiveItens } from "../../../../theme";
import FloatingLabel from "react-bootstrap/esm/FloatingLabel";

export interface IGenericFormRefProps {
  getForm: () => any;
}

interface IGenericFormProps {
  groups: IGroupProps[];
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<FieldValues>;
  _form: any;
  errors: FieldErrors<FieldValues>;
  trigger: any;
  control: Control<FieldValues, any>;
  disabled?: boolean;
  noTrigger?: boolean;
  containerStyle?: React.CSSProperties;
}

const GenericFormComponent: React.ForwardRefRenderFunction<
  IGenericFormRefProps,
  IGenericFormProps
> = (props, ref) => {
  const {
    register,
    setValue,
    errors,
    control,
    _form,
    groups,
    disabled,
    trigger,
    noTrigger = false,
    containerStyle = {},
  } = props;

  const { theme } = useGlobalWebCheckinUser();

  const getForm = useCallback(() => {
    let correct_form: any = {};

    groups.forEach((group) => {
      if (group.name) correct_form[group.name] = {};
      group.fields.forEach((fields) => {
        fields.forEach((field) => {
          const _form_obj = group.name
            ? correct_form[group.name]
            : correct_form;
          if (group.uniqueId) _form_obj.uniqueId = group.uniqueId;

          if (field.canSee ? field.canSee(_form) : true) {
            _form_obj[field.name] = !valueIsEmpty(_form[field.name])
              ? _form[field.name]
              : null;
            if (field.type === "file") return;

            const mask = field?.mask;
            const type = _form_obj[field.name]?.type;

            const _field = _form_obj[field.name];
            let value = _form_obj[field.name]?.value;

            if (Array.isArray(_field))
              value = _field.map(({ value }: any) => ({ id: value }));
            else {
              value = !valueIsEmpty(value)
                ? value
                : typeof _field === "object"
                ? null
                : _field;
              if (mask) value = maskFunctions[mask].unMask(value);
            }

            _form_obj[field.name] = value;

            if (field.type === "input-porcen-or-money") {
              if (type === "PORCEN") value = maskFunctions.porcen.unMask(value);
              else value = maskFunctions.currency.unMask(value);

              delete _form_obj[field.name];
              _form_obj[field.name + "_value"] = value ?? 0;
              _form_obj[field.name + "_type"] = type ?? "PORCEN";
            }

            if (field.type === "input-day-or-month") {
              value = maskFunctions.int.unMask(value);

              delete _form_obj[field.name];
              _form_obj[field.name + "_value"] = value ?? 0;
              _form_obj[field.name + "_type"] = type ?? "day";
            }
          } else _form_obj[field.name] = null;
        });
      });
    });
    return correct_form;
  }, [_form]);

  const getSelectFields = useCallback(() => {
    let form_obj: any = {};
    groups.forEach((group) => {
      group.fields.forEach((fields) => {
        fields.forEach((field) => {
          if (field.canSee ? field.canSee(_form) : true)
            form_obj[field.name] = _form[field.name] ?? null;
        });
      });
    });

    let form: any[] = [];

    Object.keys(form_obj).forEach((key) => {
      if (Array.isArray(form_obj[key]))
        form_obj[key].forEach((field: any) => form.push(field));
      else form.push(form_obj[key]);
    });

    form = form.filter((field: any) => !!field?.label || !!field?.type);

    return form.map((field: any) => ({
      label: field?.label,
      type: field?.type,
    }));
  }, [_form]);

  const defaultFormatOptionLabel = useCallback((value: any, options = []) => {
    if (
      !!options?.find &&
      !!options?.find((item: any) => item.value === value.value)
    ) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginRight: 10,
            color: "#FFF",
            alignItems: "center",
            gap: 5,
          }}
        >
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: 600,
            }}
          >
            {value.label}
          </div>
        </div>
      );
    } else {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginRight: 10,
            alignItems: "center",
            gap: 5,
          }}
        >
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value.label}
          </div>
        </div>
      );
    }
  }, []);

  useImperativeHandle(ref, () => ({ getForm }));

  useEffect(() => {
    if (!noTrigger) trigger();
  }, []);

  return (
    <ContainerForm style={{ ...containerStyle }}>
      {groups.map((group) => {
        const { canSee } = group;
        if (canSee ? canSee(_form) : true) {
          return (
            <ContainerGroup key={group?.name ?? group?.uniqueId}>
              {group.label && <GroupTitle>{group.label}</GroupTitle>}

              {group.fields.map((fields, index) => {
                if (
                  fields.filter((field) =>
                    field.canSee ? field.canSee(_form) : true
                  ).length === 0
                )
                  return;
                return (
                  <FieldsLine key={group.name + index}>
                    {fields.map((_field) => {
                      let {
                        options,
                        getOptions,
                        required,
                        mask,
                        numberOfRows = 5,
                        formatOptionLabel = defaultFormatOptionLabel,
                        isPassword,
                      } = _field;
                      const {
                        getFileSizeMax = () => undefined,
                        getFileTypes = () => [],
                        icon,
                      } = _field;
                      const {
                        onClick,
                        executeOnChange = async () => {},
                        getRemoveIds = () => [],
                      } = _field;
                      const {
                        searchOptions,
                        validate,
                        isClearable,
                        moreQueries,
                      } = _field;
                      const {
                        getIsDisabled = () => false,
                        additionalsQueries = () => {},
                      } = _field;
                      const {
                        name,
                        label,
                        type,
                        noUpperCase,
                        canSee,
                        onRemove,
                      } = _field;

                      options = getOptions ? getOptions(_form) : options;

                      const fileSizeMax = getFileSizeMax(_form);

                      const fileTypes = getFileTypes(_form);

                      const _disabled = disabled ? true : getIsDisabled(_form);

                      const removeIds = getRemoveIds(_form).filter(
                        (id: any) => !!id
                      );

                      required = required ? "Campo Obrigatório" : undefined;

                      const _additionalsQueries = additionalsQueries
                        ? additionalsQueries(_form)
                        : {};

                      const _searchOptions = async (search: string) => {
                        return await searchOptions({
                          search,
                          ...moreQueries,
                          ..._additionalsQueries,
                          removeIds,
                        });
                      };

                      const _executeOnChange = async (value: any) => {
                        await executeOnChange(value);
                        // trigger();
                      };

                      const handleChange = (props: any) => {
                        const { value, field } = props;
                        if (!value && onRemove) onRemove();
                        field?.onChange(value);
                        _executeOnChange(value);
                      };

                      if (canSee ? canSee(_form) : true)
                        return (
                          <Column
                            key={
                              name +
                              JSON.stringify(getSelectFields()) +
                              JSON.stringify(removeIds)
                            }
                            style={{ flex: 1, minWidth: 250 }}
                          >
                            <Column>
                              <Line style={{ alignItems: "center" }}>
                                {_field.tooltip && (
                                  <Tooltip
                                    placement="top"
                                    style={{
                                      padding: 0,
                                      marginLeft: 5,
                                      marginTop: -4,
                                    }}
                                    title={_field.tooltip}
                                  >
                                    <IconButton aria-label={_field.tooltip}>
                                      <FiInfo
                                        color={inactiveItens[theme]}
                                        size={15}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Line>
                              {type === "input" && (
                                <Input
                                  noUpperCase={noUpperCase}
                                  name={name}
                                  mask={mask}
                                  disabled={_disabled}
                                  options={{
                                    ...register(name, { required, validate }),
                                  }}
                                  label={label}
                                  error={errors?.[name]?.message}
                                  onChange={_executeOnChange}
                                  isPassword={isPassword}
                                />
                              )}
                              {type === "select-fixed" && (
                                <Controller
                                  name={name}
                                  control={control}
                                  rules={{ required, validate }}
                                  render={({
                                    field,
                                    fieldState: { invalid, error },
                                  }) => (
                                    <FloatingLabel label={label} style={{fontSize: '14px'}}>
                                      <BaseSelect
                                        haveError={!!invalid}
                                        className="form-control"
                                        {...field}
                                        onChange={(e: any) => {
                                          const value = options.find(
                                            (item: any) =>
                                              item.value === e.target.value
                                          );
                                          handleChange({
                                            value,
                                            field,
                                          });
                                        }}
                                        value={field?.value?.value}
                                      >
                                        {options.map(
                                          ({
                                            value,
                                            label: optionLabel,
                                          }: any) => {
                                            return (
                                              <option
                                                key={label + value}
                                                selected={
                                                  field?.value?.value === value
                                                }
                                                value={value}
                                              >
                                                {optionLabel}
                                              </option>
                                            );
                                          }
                                        )}
                                      </BaseSelect>
                                    </FloatingLabel>
                                  )}
                                />
                              )}
                            </Column>
                          </Column>
                        );
                    })}
                  </FieldsLine>
                );
              })}
            </ContainerGroup>
          );
        }
      })}
    </ContainerForm>
  );
};

export const GenericFormWebCheckinUser = forwardRef(GenericFormComponent);
